import {
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  computed,
  onUnmounted,
  ref,
  toValue,
  watch,
} from 'vue';
import { IntakeAddressService } from '@/core/shared/intake-address/intake-address.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { SNACKBAR_TIMEOUT_SECONDS_3 } from '@/core/shared/snackbar/snackbar.store';
import { copyToClipboard } from '@/utils/clipboard-utils';

// The anchored section ships with the help-site change for email intake; until
// that deploys, the link degrades gracefully to the top of the article.
export const EMAIL_FORWARDING_LEARN_MORE_URL =
  'https://help.factoryfix.com/articles/managing-candidates/upload-resumes-to-a-job#email-candidates-to-a-job';

// Middle-truncate just the token, e.g. "11815+XM33...OEJE@intake.factoryfix.com" --
// keeps jobId and the full trusted domain always visible.
const TOKEN_VISIBLE_CHARS = 4;

export type UseIntakeAddress = {
  address: Ref<string | null>;
  displayAddress: ComputedRef<string>;
  isLoading: Ref<boolean>;
  hasError: Ref<boolean>;
  justCopied: Ref<boolean>;
  fetchAddress: () => Promise<void>;
  handleCopy: () => Promise<void>;
};

/**
 * Loads the per-job email intake address and exposes it with copy support.
 * Refetches when the job changes. Must be called during component setup.
 */
export function useIntakeAddress(
  jobId: MaybeRefOrGetter<number | null | undefined>,
): UseIntakeAddress {
  const intakeAddressService = new IntakeAddressService();

  const address = ref<string | null>(null);
  const isLoading = ref(false);
  const hasError = ref(false);
  const justCopied = ref(false);

  let copiedTimeoutId: ReturnType<typeof setTimeout> | undefined;

  const displayAddress = computed(() => {
    if (!address.value) return '';

    const atIndex = address.value.indexOf('@');
    const plusIndex = address.value.indexOf('+');
    if (atIndex === -1 || plusIndex === -1 || plusIndex >= atIndex) return address.value;

    const prefix = address.value.slice(0, plusIndex + 1);
    const token = address.value.slice(plusIndex + 1, atIndex);
    const domain = address.value.slice(atIndex);
    if (token.length <= TOKEN_VISIBLE_CHARS * 2) return address.value;

    const start = token.slice(0, TOKEN_VISIBLE_CHARS);
    const end = token.slice(-TOKEN_VISIBLE_CHARS);
    return `${prefix}${start}...${end}${domain}`;
  });

  async function fetchAddress(): Promise<void> {
    const requestedJobId = toValue(jobId);
    if (requestedJobId === null || requestedJobId === undefined) {
      // A fetch still in flight for a previous job skips its own state updates
      // once the jobId stops matching, so everything must be reset here or a
      // caller whose jobId became null would be stuck on the loading state.
      isLoading.value = false;
      hasError.value = false;
      address.value = null;
      justCopied.value = false;
      clearTimeout(copiedTimeoutId);
      return;
    }

    isLoading.value = true;
    hasError.value = false;
    // Clear the previous job's address up front: if this fetch fails, a stale
    // address must not remain visible (and copyable) next to the error message.
    address.value = null;
    justCopied.value = false;
    clearTimeout(copiedTimeoutId);
    try {
      const result = await intakeAddressService.getIntakeAddress(requestedJobId);
      if (toValue(jobId) === requestedJobId) {
        address.value = result;
      }
    } catch (error) {
      if (toValue(jobId) === requestedJobId) {
        hasError.value = true;
        ErrorService.captureException(error);
      }
    } finally {
      if (toValue(jobId) === requestedJobId) {
        isLoading.value = false;
      }
    }
  }

  watch(() => toValue(jobId), fetchAddress, { immediate: true });

  async function handleCopy(): Promise<void> {
    const copiedAddress = address.value;
    if (!copiedAddress) return;
    await copyToClipboard(copiedAddress);

    // The clipboard write can settle after the component unmounted or after the
    // address changed to another job's; confirming then would mislabel it.
    if (isDisposed || address.value !== copiedAddress) return;
    SnackbarService.success('Address copied to clipboard');

    justCopied.value = true;
    clearTimeout(copiedTimeoutId);
    copiedTimeoutId = setTimeout(() => {
      justCopied.value = false;
    }, SNACKBAR_TIMEOUT_SECONDS_3);
  }

  let isDisposed = false;
  onUnmounted(() => {
    isDisposed = true;
    clearTimeout(copiedTimeoutId);
  });

  return {
    address,
    displayAddress,
    isLoading,
    hasError,
    justCopied,
    fetchAddress,
    handleCopy,
  };
}
